// Interactive 3D model: the Revit building (walls with their openings, slabs, ceilings at their modelled
// heights, rooms by apartment) and the AC design on it — wall heads, outdoor units on the roof deck,
// refrigerant line sets, condensate and exhaust routes. Click anything for its data.
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { CSS2DObject, CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { buildBuilding, type Building, type P2, type Room } from "../model/building";
import { designHvac, type BranchBox, type Chase, type Design, type ExhaustFan, type Group, type IndoorUnit, type OutdoorUnit, type Rack, type Run, type Shaft } from "../design/hvac";
import { ftIn, type P3, type RevitModel } from "../model/schema";
import { buildBomWorkbook, catOf } from "../bom/workbook";

type Pick =
  | { kind: "room"; room: Room } | { kind: "iu"; u: IndoorUnit } | { kind: "odu"; o: OutdoorUnit }
  | { kind: "run"; r: Run } | { kind: "fan"; f: ExhaustFan } | { kind: "box"; bx: BranchBox } | { kind: "rack"; rack: Rack } | { kind: "chase"; ch: Chase } | { kind: "shaft"; sh: Shaft }
  | { kind: "equip"; title: string; sub: string; rows: [string, string][] };
const ALWAYS = -1; // racks, outdoor units and chases show whichever storey is selected

const LAYERS: { key: string; label: string; color: string; on: boolean }[] = [
  { key: "walls", label: "Walls", color: "#e9e6e1", on: true },
  { key: "glazing", label: "Windows", color: "#9cc8e6", on: true },
  { key: "floors", label: "Floor slabs", color: "#c9ccd0", on: true },
  { key: "roof", label: "Roof slab", color: "#b8bcc1", on: false },
  { key: "ceilings", label: "Ceilings (at modelled height)", color: "#ffffff", on: true },
  { key: "rooms", label: "Rooms by apartment", color: "#f3d9c4", on: true },
  { key: "columns", label: "Columns", color: "#b9b4ac", on: true },
  { key: "heads", label: "Cassettes (4-way / 1-way)", color: "#19a3a3", on: true },
  { key: "boxes", label: "Branch boxes", color: "#7c3aed", on: true },
  { key: "outdoor", label: "Outdoor units", color: "#3d4a57", on: true },
  { key: "racks", label: "Outdoor-unit racks", color: "#9a8f80", on: true },
  { key: "chases", label: "Line-set chases", color: "#d8b4fe", on: true },
  { key: "shafts", label: "General riser shaft", color: "#94a3b8", on: true },
  { key: "refrigerant", label: "Refrigerant line sets", color: "#e07b2a", on: true },
  { key: "condensate", label: "Condensate drainage", color: "#2f86d6", on: true },
  { key: "fans", label: "Exhaust fans", color: "#cfd6dc", on: true },
  { key: "exhaust", label: "Bathroom extract ducts", color: "#8a96a3", on: true },
  { key: "plant", label: "Other plant (pumps, heat pump)", color: "#a7b0b9", on: true },
  { key: "removed", label: "Modelled wall heads (to remove)", color: "#d94b4b", on: false },
  { key: "fixtures", label: "Plumbing fixtures", color: "#dfe3e7", on: false },
  { key: "plumbing", label: "Plumbing pipes (context)", color: "#7fb07f", on: false },
  { key: "labels", label: "Tags", color: "#1d2329", on: true },
];
const APT_COLORS = ["#f3d9c4", "#d8e7c8", "#cfe0ef", "#ead3e6", "#f1e6b8", "#d3ece6", "#e8d6c8", "#dcd7ef", "#f0d0cf", "#cde6d4", "#e6e1cf", "#d0dcea"];

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: unknown) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const fmt = (n: number) => n.toLocaleString("en-US");

async function main() {
  const res = await fetch("model.json");
  const model: RevitModel = await res.json();
  const b = buildBuilding(model);
  const d = designHvac(b);
  new Viewer(b, d).start();
  // the construction set is built here in the browser from the same model and design, so it always matches the 3D view
  const btn = $<HTMLButtonElement>("download"), note = $("dlnote");
  const modified = res.headers.get("last-modified"); const stamp = (modified ? new Date(modified) : new Date()).toISOString().slice(0, 16).replace("T", " ");
  btn.disabled = false; note.textContent = "ARCH E1 · built in your browser from this model";
  const xb = $<HTMLButtonElement>("downloadXlsx"); xb.disabled = false;
  xb.addEventListener("click", async () => {
    xb.disabled = true;
    try {
      const ExcelJS = (await import("exceljs")).default;
      const { defaultTitle } = await import("../sheets/titleblock");
      const buf = await buildBomWorkbook(ExcelJS, b, d, defaultTitle(), stamp);
      const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = `${(b.model.title || "HVAC").replace(/\.rvt$/i, "").replace(/[^\w\- ]+/g, "").trim()} - HVAC bill of materials.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      const n = (k: string) => d.bom.filter((l) => catOf(l) === k).length;
      note.textContent = `Bill of materials: ${n("units")} unit lines, ${n("copper")} copper, ${n("condensate")} condensate, ${n("duct")} duct, ${n("racks")} rack lines, with run-by-run take-offs`;
    } catch (e) { note.textContent = `Could not build the spreadsheet: ${(e as Error).message}`; }
    finally { xb.disabled = false; }
  });
  btn.addEventListener("click", async () => {
    btn.disabled = true; const label = btn.textContent; btn.textContent = "Building the set…";
    try {
      const [{ buildSet }, { defaultTitle }] = await Promise.all([import("../sheets/set"), import("../sheets/titleblock")]);
      await new Promise((r) => setTimeout(r, 30));
      const { pdf, drawn } = buildSet(b, d, defaultTitle(), stamp, "revit-model.json", undefined, (i, n, t) => { note.textContent = `Sheet ${i + 1} of ${n}: ${t}`; });
      const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${(b.model.title || "HVAC").replace(/\.rvt$/i, "").replace(/[^\w\- ]+/g, "").trim()} - HVAC set.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      note.textContent = `Downloaded ${drawn.length} sheets (${(pdf.byteLength / 1e6).toFixed(1)} MB)`;
    } catch (e) { note.textContent = `Could not build the set: ${(e as Error).message}`; console.error(e); }
    finally { btn.disabled = false; btn.textContent = label; }
  });
}

class Viewer {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  labels: CSS2DRenderer;
  controls: OrbitControls;
  objects: THREE.Object3D[] = [];
  pickables: THREE.Object3D[] = [];
  layerOn = Object.fromEntries(LAYERS.map((l) => [l.key, l.on])) as Record<string, boolean>;
  storey: number | "all" = "all";
  below = true;
  mats: Record<string, THREE.Material> = {};
  runMeshes = new Map<string, THREE.Mesh>();
  selected: Set<string> | null = null;
  cx: number; cy: number; z0: number;
  aptColor = new Map<string, string>();

  constructor(public b: Building, public d: Design) {
    this.cx = b.centre[0]; this.cy = b.centre[1]; this.z0 = b.storeys[0].elev;
    const stage = $("stage");
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setClearColor(0xeef0f2);
    stage.appendChild(this.renderer.domElement);
    this.labels = new CSS2DRenderer();
    Object.assign(this.labels.domElement.style, { position: "absolute", top: "0", left: "0", pointerEvents: "none" });
    stage.appendChild(this.labels.domElement);
    this.camera = new THREE.PerspectiveCamera(40, 1, 0.5, 4000);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xb8bcc4, 2.2));
    const sun = new THREE.DirectionalLight(0xffffff, 1.6); sun.position.set(-80, 160, 120); this.scene.add(sun);
    let i = 0; for (const g of d.groups) if (g.apt && !this.aptColor.has(g.apt)) this.aptColor.set(g.apt, APT_COLORS[i++ % APT_COLORS.length]);
    for (const r of b.rooms) if (r.apt && !this.aptColor.has(r.apt)) this.aptColor.set(r.apt, APT_COLORS[i++ % APT_COLORS.length]);
  }

  v = (p: P3 | [number, number, number]) => new THREE.Vector3(p[0] - this.cx, p[2] - this.z0, -(p[1] - this.cy));

  mat(key: string, make: () => THREE.Material) { return (this.mats[key] ??= make()); }

  add(o: THREE.Object3D, layer: string, storey: number, pick?: Pick, tags: string[] = []) {
    o.userData = { layer, storey, pick, tags };
    this.scene.add(o); this.objects.push(o);
    if (pick) this.pickables.push(o);
    return o;
  }

  label(text: string, at: THREE.Vector3, storey: number, cls = "", tags: string[] = []) {
    const el = document.createElement("div"); el.className = `lbl ${cls}`; el.textContent = text;
    const o = new CSS2DObject(el); o.position.copy(at);
    this.add(o, "labels", storey, undefined, tags);
  }

  // ---------------------------------------------------------------- architecture
  buildArchitecture() {
    const { b } = this; const m = b.model; const S = b.storeys;
    const range = (i: number): [number, number] => [i === 0 ? -1e9 : S[i].elev, i === S.length - 1 ? 1e9 : S[i + 1].elev];
    const wallGeo: THREE.BufferGeometry[][] = S.map(() => []); const glassGeo: THREE.BufferGeometry[][] = S.map(() => []);
    const box = (a: P2, dir: P2, s0: number, s1: number, thk: number, z0: number, z1: number, out: THREE.BufferGeometry[]) => {
      const L = s1 - s0; if (L < 0.02 || z1 - z0 < 0.02) return;
      const g = new THREE.BoxGeometry(L, z1 - z0, thk);
      const mid: P3 = [a[0] + dir[0] * (s0 + L / 2), a[1] + dir[1] * (s0 + L / 2), (z0 + z1) / 2];
      g.applyMatrix4(new THREE.Matrix4().compose(this.v(mid), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.atan2(dir[1], dir[0])), new THREE.Vector3(1, 1, 1)));
      out.push(g);
    };
    const inserts = [...m.doors.map((o) => ({ o, door: true })), ...m.windows.map((o) => ({ o, door: false }))];
    for (const w of m.walls) {
      if (!w.bbox) continue;
      const wz0 = w.bbox.min[2], wz1 = w.bbox.max[2];
      const pts = w.centreline.map((p) => [p[0], p[1]] as P2);
      const mine = inserts.filter((x) => x.o.host === w.id && x.o.location && x.o.bbox);
      for (let k = 0; k + 1 < pts.length; k++) {
        const a = pts[k], c = pts[k + 1]; const L = Math.hypot(c[0] - a[0], c[1] - a[1]); if (L < 0.01) continue;
        const dir: P2 = [(c[0] - a[0]) / L, (c[1] - a[1]) / L];
        const ops = mine.flatMap(({ o, door }) => {
          const loc = o.location!; const s = (loc[0] - a[0]) * dir[0] + (loc[1] - a[1]) * dir[1];
          const off = Math.abs(-(loc[0] - a[0]) * dir[1] + (loc[1] - a[1]) * dir[0]);
          const bb = o.bbox!; const wd = o.widthFt ?? Math.abs((bb.max[0] - bb.min[0]) * dir[0]) + Math.abs((bb.max[1] - bb.min[1]) * dir[1]);
          if (off > w.thicknessFt + 1 || s < -wd / 2 || s > L + wd / 2) return [];
          const sill = door ? wz0 : bb.min[2]; const head = Math.min(bb.max[2], wz1);
          return [{ s0: Math.max(0, s - wd / 2), s1: Math.min(L, s + wd / 2), sill, head, door }];
        }).sort((x, y) => x.s0 - y.s0);
        for (let i = 0; i < S.length; i++) {
          const [r0, r1] = range(i); const z0 = Math.max(wz0, r0), z1 = Math.min(wz1, r1); if (z1 - z0 < 0.05) continue;
          let s = 0;
          for (const op of ops) {
            if (op.s0 > s) box(a, dir, s, op.s0, w.thicknessFt, z0, z1, wallGeo[i]);
            const lo = Math.max(op.s0, s);
            box(a, dir, lo, op.s1, w.thicknessFt, z0, Math.min(z1, Math.max(z0, op.sill)), wallGeo[i]);
            box(a, dir, lo, op.s1, w.thicknessFt, Math.max(z0, Math.min(z1, op.head)), z1, wallGeo[i]);
            if (!op.door) box(a, dir, lo, op.s1, 0.06, Math.max(z0, op.sill), Math.min(z1, op.head), glassGeo[i]);
            s = Math.max(s, op.s1);
          }
          if (s < L) box(a, dir, s, L, w.thicknessFt, z0, z1, wallGeo[i]);
        }
      }
    }
    const wallMat = this.mat("wall", () => new THREE.MeshLambertMaterial({ color: 0xe9e6e1, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 }));
    const edgeMat = this.mat("edge", () => new THREE.LineBasicMaterial({ color: 0x8b8f94, transparent: true, opacity: 0.55 }));
    const glassMat = this.mat("glass", () => new THREE.MeshLambertMaterial({ color: 0x9cc8e6, transparent: true, opacity: 0.35, depthWrite: false }));
    S.forEach((_, i) => {
      if (wallGeo[i].length) {
        const g = mergeGeometries(wallGeo[i]);
        this.add(new THREE.Mesh(g, wallMat), "walls", i);
        this.add(new THREE.LineSegments(new THREE.EdgesGeometry(g, 25), edgeMat), "walls", i);
      }
      if (glassGeo[i].length) this.add(new THREE.Mesh(mergeGeometries(glassGeo[i]), glassMat), "glazing", i);
    });

    // slabs: extruded sketch outlines (outer loop + holes), top at the bbox top
    const levelIdx = new Map(S.map((s) => [s.name, s.index]));
    for (const f of m.floors) {
      if (!f.bbox || !f.loops.length || f.loops[0].length < 3) continue;
      const top = f.bbox.max[2]; const thk = Math.max(0.2, Math.min(f.thicknessFt ?? f.bbox.max[2] - f.bbox.min[2], 2));
      const onStorey = f.level != null && levelIdx.has(f.level);
      const roof = !onStorey && top > S[S.length - 1].elev + 3;
      const geo = this.slab(f.loops, thk); if (!geo) continue;
      geo.translate(0, top - thk - this.z0, 0);
      const color = /grass/i.test(f.type) ? 0xb9cf9e : /pave|deck/i.test(f.type) ? 0xd9d2c3 : roof ? 0xb8bcc1 : 0xc9ccd0;
      const mesh = new THREE.Mesh(geo, this.mat(`slab${color}`, () => new THREE.MeshLambertMaterial({ color, polygonOffset: true, polygonOffsetFactor: 2, polygonOffsetUnits: 2 })));
      const st = onStorey ? levelIdx.get(f.level!)! : b.storeyOfZ(top);
      this.add(mesh, roof ? "roof" : "floors", st);
    }

    // ceilings at their modelled height
    const ceilMat = this.mat("ceiling", () => new THREE.MeshLambertMaterial({ color: 0xffffff, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false }));
    const byName = new Map(m.levels.map((l) => [l.name, l.elevation]));
    for (const c of m.ceilings) {
      if (!c.loops.length || c.loops[0].length < 3) continue;
      const z = c.level && byName.has(c.level) && c.heightAboveLevelFt != null ? byName.get(c.level)! + c.heightAboveLevelFt : c.bbox?.min[2];
      if (z == null) continue;
      const g = this.flat(c.loops.map((l) => l.map((p) => [p[0], p[1]] as P2))); if (!g) continue;
      g.translate(0, z - this.z0, 0);
      this.add(new THREE.Mesh(g, ceilMat), "ceilings", b.storeyOfZ(z - 6));
    }

    // rooms: floor tint by apartment, pickable
    for (const r of b.rooms) {
      const g = this.flat([r.poly, ...r.holes]); if (!g) continue;
      const st = b.storeys[r.storey]; g.translate(0, st.elev + 0.06 - this.z0, 0);
      const color = r.apt ? this.aptColor.get(r.apt)! : "#e4e6e8";
      const mesh = new THREE.Mesh(g, this.mat(`room${color}`, () => new THREE.MeshLambertMaterial({ color, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })));
      this.add(mesh, "rooms", r.storey, { kind: "room", room: r });
    }
    for (const s of b.storeys) this.label(s.short, this.v([b.bounds.min[0] - 4, b.bounds.min[1] - 4, s.elev + 1]), s.index, "storey");

    // columns, other plant, plumbing fixtures as boxes
    const colMat = this.mat("col", () => new THREE.MeshLambertMaterial({ color: 0xb9b4ac }));
    for (const c of m.columns) {
      if (!c.bbox) continue;
      for (let i = 0; i < S.length; i++) {
        const [r0, r1] = range(i); const z0 = Math.max(c.bbox.min[2], r0), z1 = Math.min(c.bbox.max[2], r1);
        if (z1 - z0 > 0.1) this.add(this.boxMesh({ min: [c.bbox.min[0], c.bbox.min[1], z0], max: [c.bbox.max[0], c.bbox.max[1], z1] }, colMat), "columns", i);
      }
    }
    const designIds = new Set([...this.d.indoor.map((u) => u.id), ...this.d.fans.map((f) => f.id)]);
    const replaced = new Set(this.d.replacedHeads.map((e) => e.id));
    const oldMat = this.mat("old", () => new THREE.MeshLambertMaterial({ color: 0xd94b4b, transparent: true, opacity: 0.6 }));
    for (const e of m.equipment) {
      if (!e.bbox || designIds.has(e.id)) continue;
      if (replaced.has(e.id)) {
        this.add(this.boxMesh(e.bbox, oldMat), "removed", b.storeyOfZ(e.bbox.min[2] - 6),
          { kind: "equip", title: `${e.type} (to be removed)`, sub: e.family ?? "", rows: [["Room", e.room ?? "—"], ["Why", "replaced by the ceiling cassette in this room"]] });
        continue;
      }
      const plant = e.category === "mechanical";
      const mm = this.mat(plant ? "plant" : "fixture", () => new THREE.MeshLambertMaterial({ color: plant ? 0xa7b0b9 : 0xdfe3e7 }));
      this.add(this.boxMesh(e.bbox, mm), plant ? "plant" : "fixtures", b.storeyOfZ(e.bbox.min[2] + 0.5),
        { kind: "equip", title: e.type ?? "Equipment", sub: e.family ?? "", rows: [["Category", e.category], ["Room", e.room ?? "—"], ["Elevation", ftIn(e.bbox.min[2])]] });
    }

    // plumbing pipes as thin lines (context for clashes)
    const sysCol = (s: string | null) => /COLD|Domestic Cold/i.test(s ?? "") ? 0x3b82c4 : /HOT|TEMPERED/i.test(s ?? "") ? 0xd2453b : /VENT/i.test(s ?? "") ? 0x8fa05a : /SANITARY|GW|BW/i.test(s ?? "") ? 0x8a6a4a : /STORM/i.test(s ?? "") ? 0x4aa0a0 : 0xa070c0;
    const byStoreyCol = new Map<string, number[]>();
    for (const p of m.pipes) {
      if (p.pts.length < 2) continue;
      const st = b.storeyOfZ(Math.min(...p.pts.map((q) => q[2])) + 0.5); const k = `${st}:${sysCol(p.systemType)}`;
      const arr = byStoreyCol.get(k) ?? []; byStoreyCol.set(k, arr);
      for (let i = 0; i + 1 < p.pts.length; i++) arr.push(...this.v(p.pts[i]).toArray(), ...this.v(p.pts[i + 1]).toArray());
    }
    for (const [k, arr] of byStoreyCol) {
      const [st, col] = k.split(":").map(Number);
      const g = new THREE.BufferGeometry(); g.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
      this.add(new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: col })), "plumbing", st);
    }
  }

  shape(loops: P2[][]) {
    const s = new THREE.Shape(loops[0].map((p) => new THREE.Vector2(p[0] - this.cx, p[1] - this.cy)));
    for (const h of loops.slice(1)) if (h.length > 2) s.holes.push(new THREE.Path(h.map((p) => new THREE.Vector2(p[0] - this.cx, p[1] - this.cy))));
    return s;
  }
  slab(loops: P3[][], thk: number) {
    try {
      const g = new THREE.ExtrudeGeometry(this.shape(loops.map((l) => l.map((p) => [p[0], p[1]] as P2))), { depth: thk, bevelEnabled: false });
      g.rotateX(-Math.PI / 2); return g;
    } catch { return null; }
  }
  flat(loops: P2[][]) {
    try { const g = new THREE.ShapeGeometry(this.shape(loops)); g.rotateX(-Math.PI / 2); return g; } catch { return null; }
  }
  boxMesh(bb: { min: P3; max: P3 }, m: THREE.Material) {
    const s = [bb.max[0] - bb.min[0], bb.max[2] - bb.min[2], bb.max[1] - bb.min[1]].map((x) => Math.max(x, 0.05));
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(s[0], s[1], s[2]), m);
    mesh.position.copy(this.v([(bb.min[0] + bb.max[0]) / 2, (bb.min[1] + bb.max[1]) / 2, (bb.min[2] + bb.max[2]) / 2]));
    return mesh;
  }

  // ---------------------------------------------------------------- HVAC
  buildHvac() {
    const { d, b } = this; const top = b.storeys.length - 1;
    // cassettes: body in the void, white panel at the ceiling
    const bodyMat = this.mat("iu", () => new THREE.MeshLambertMaterial({ color: 0xdfe6e6 }));
    const panelMat = this.mat("panel", () => new THREE.MeshLambertMaterial({ color: 0xffffff, emissive: 0x0b4f4f, emissiveIntensity: 0.15 }));
    const iuEdge = this.mat("iuEdge", () => new THREE.LineBasicMaterial({ color: 0x19a3a3 }));
    for (const u of d.indoor) {
      const body = this.boxMesh({ min: [u.bbox.min[0] + 0.08, u.bbox.min[1] + 0.08, u.pos[2]], max: [u.bbox.max[0] - 0.08, u.bbox.max[1] - 0.08, u.bbox.max[2]] }, bodyMat);
      this.add(body, "heads", u.storey, { kind: "iu", u }, [u.tag, u.group]);
      const panel = this.boxMesh({ min: u.bbox.min, max: [u.bbox.max[0], u.bbox.max[1], u.pos[2]] }, panelMat);
      for (const bl of u.blow) {
        const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.9, 12), this.mat("blow", () => new THREE.MeshLambertMaterial({ color: 0x19a3a3 })));
        const edge = bl[0] ? u.panel[0] / 2 : u.panel[1] / 2; const at = this.v([u.pos[0] + bl[0] * (edge + 0.55), u.pos[1] + bl[1] * (edge + 0.55), u.pos[2] - 0.35]);
        arrow.position.copy(at); arrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(bl[0], -0.35, -bl[1]).normalize());
        this.add(arrow, "heads", u.storey, undefined, [u.tag, u.group]);
      }
      this.add(panel, "heads", u.storey, { kind: "iu", u }, [u.tag, u.group]);
      const e = new THREE.LineSegments(new THREE.EdgesGeometry(panel.geometry), iuEdge); e.position.copy(panel.position);
      this.add(e, "heads", u.storey, undefined, [u.tag, u.group]);
      this.label(u.tag, this.v([u.pos[0], u.pos[1], u.pos[2] - 0.7]), u.storey, "", [u.tag, u.group]);
    }
    const boxMat = this.mat("bb", () => new THREE.MeshLambertMaterial({ color: 0x7c3aed }));
    for (const bx of d.boxes) {
      const [w, dp, h] = bx.size;
      this.add(this.boxMesh({ min: [bx.pos[0] - w / 2, bx.pos[1] - dp / 2, bx.pos[2]], max: [bx.pos[0] + w / 2, bx.pos[1] + dp / 2, bx.pos[2] + h] }, boxMat), "boxes", bx.storey, { kind: "box", bx }, [bx.tag, bx.group]);
      this.label(bx.tag, this.v([bx.pos[0], bx.pos[1], bx.pos[2] + h + 0.5]), bx.storey, "", [bx.tag, bx.group]);
    }
    // racks: a steel frame with a shelf per tier
    const frameMat = this.mat("rack", () => new THREE.LineBasicMaterial({ color: 0x6b6256 }));
    const shelfMat = this.mat("shelf", () => new THREE.MeshLambertMaterial({ color: 0x9a8f80 }));
    for (const r of d.racks) {
      const xs = r.corners.map((p) => p[0]), ys = r.corners.map((p) => p[1]);
      const hgt = r.legs + r.tiers * r.tierH;
      const frame = this.boxMesh({ min: [Math.min(...xs), Math.min(...ys), r.z], max: [Math.max(...xs), Math.max(...ys), r.z + hgt] }, shelfMat);
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(frame.geometry), frameMat); edges.position.copy(frame.position);
      this.add(edges, "racks", ALWAYS, undefined, [r.tag]);
      for (let t = 0; t < r.tiers; t++) {
        const z = r.z + r.legs + t * r.tierH;
        this.add(this.boxMesh({ min: [Math.min(...xs), Math.min(...ys), z - 0.15], max: [Math.max(...xs), Math.max(...ys), z] }, shelfMat), "racks", ALWAYS, { kind: "rack", rack: r }, [r.tag]);
      }
      this.label(`${r.tag} · ${r.name.replace(/ \(.*/, "")}`, this.v([(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2, r.z + hgt + 1.2]), ALWAYS, "odu", [r.tag]);
    }
    const oduMat = this.mat("odu", () => new THREE.MeshLambertMaterial({ color: 0x8d99a6 }));
    const fanMat = this.mat("oduFan", () => new THREE.MeshLambertMaterial({ color: 0x2b333b }));
    for (const o of d.outdoor) {
      const [w, dp, h] = o.size;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, dp), oduMat);
      mesh.position.copy(this.v([o.pos[0], o.pos[1], o.pos[2] + h / 2]));
      // long side along the rack: rotate when the rack runs north–south
      const rot = Math.abs(o.facing[0]) > 0.5 ? Math.PI / 2 : 0; mesh.rotation.y = rot;
      this.add(mesh, "outdoor", ALWAYS, { kind: "odu", o }, [o.tag, o.group]);
      const fan = new THREE.Mesh(new THREE.CylinderGeometry(Math.min(w, h) * 0.32, Math.min(w, h) * 0.32, 0.04, 24), fanMat);
      const n = this.v([o.facing[0], o.facing[1], 0]).sub(this.v([0, 0, 0])).normalize();
      fan.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n); fan.position.copy(mesh.position).add(n.multiplyScalar(dp / 2 + 0.02));
      this.add(fan, "outdoor", ALWAYS, undefined, [o.tag, o.group]);
      this.label(`${o.tag} · ${o.group}`, this.v([o.pos[0], o.pos[1], o.pos[2] + h + 0.4]), ALWAYS, "", [o.tag, o.group]);
    }
    // chases: translucent shafts over their height
    const chMat = this.mat("chase", () => new THREE.MeshLambertMaterial({ color: 0xd8b4fe, transparent: true, opacity: 0.28, depthWrite: false }));
    const shMat = this.mat("shaft", () => new THREE.MeshLambertMaterial({ color: 0x94a3b8, transparent: true, opacity: 0.35, depthWrite: false }));
    for (const sh of d.shafts) {
      this.add(this.boxMesh({ min: [sh.rect.x0, sh.rect.y0, sh.z0], max: [sh.rect.x1, sh.rect.y1, sh.z1] }, shMat), "shafts", ALWAYS, { kind: "shaft", sh }, [sh.tag]);
      this.label(`${sh.tag} · general riser shaft`, this.v([sh.at[0], sh.at[1], sh.z1 + 0.8]), ALWAYS, "odu", [sh.tag]);
    }
    for (const ch of d.chases) {
      const [w, dp] = ch.size; const z0 = Math.min(ch.z0, ...ch.storeys.map((s) => b.storeys[s].elev)) , z1 = Math.max(ch.z1, ...ch.storeys.map((s) => s === top ? d.basis.roofZ : b.storeys[s].top));
      this.add(this.boxMesh({ min: [ch.at[0] - w / 2, ch.at[1] - dp / 2 + 0.3, z0], max: [ch.at[0] + w / 2, ch.at[1] + dp / 2 + 0.3, z1] }, chMat), "chases", ALWAYS, { kind: "chase", ch }, [ch.tag, ...ch.groups]);
      this.label(ch.tag, this.v([ch.at[0], ch.at[1], z1 + 0.6]), ALWAYS, "", [ch.tag, ...ch.groups]);
    }
    const fMat = this.mat("fan", () => new THREE.MeshLambertMaterial({ color: 0xcfd6dc }));
    for (const f of d.fans) {
      this.add(this.boxMesh(f.bbox, fMat), "fans", f.storey, { kind: "fan", f }, [f.tag]);
      this.label(f.tag, this.v([f.pos[0], f.pos[1], f.bbox.min[2] - 0.6]), f.storey, "", [f.tag]);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.7), this.mat("cap", () => new THREE.MeshLambertMaterial({ color: 0x5f6b77 })));
      cap.position.copy(this.v(f.cap)); this.add(cap, "exhaust", f.storey, undefined, [f.tag]);
    }
    const color = (r: Run) => r.kind === "condensate" ? (r.role === "branch" ? 0x60a5fa : r.role === "main" ? 0x2f86d6 : 0x1e40af) : r.kind === "exhaust" ? (r.role === "branch" ? 0xa3adb8 : r.role === "main" ? 0x7b8794 : 0x4b5563) : r.role === "indoor" ? 0xe07b2a : 0xb4531a;
    const scale = { refrigerant: 2.2, condensate: 2.2, exhaust: 1 };
    for (const r of d.runs) {
      const rad = (r.diaIn / 24) * scale[r.kind];
      const parts: THREE.BufferGeometry[] = [];
      const pts = r.pts.map((p) => this.v(p));
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i], c = pts[i + 1]; const L = a.distanceTo(c); if (L < 0.01) continue;
        const g = new THREE.CylinderGeometry(rad, rad, L, 10, 1, true);
        g.applyMatrix4(new THREE.Matrix4().compose(a.clone().add(c).multiplyScalar(0.5), new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), c.clone().sub(a).normalize()), new THREE.Vector3(1, 1, 1)));
        parts.push(g.toNonIndexed());
        if (i) { const s = new THREE.SphereGeometry(rad, 10, 8); s.translate(a.x, a.y, a.z); parts.push(s.toNonIndexed()); }
      }
      if (!parts.length) continue;
      const mesh = new THREE.Mesh(mergeGeometries(parts), new THREE.MeshLambertMaterial({ color: color(r), transparent: true, opacity: 1 }));
      const g = this.groupOfRun(r);
      // trunks that leave the storey (to the racks) show on every storey; runs inside a storey stay with it
      const storey = (r.role === "main" && r.kind === "refrigerant") || r.role === "riser" || r.role === "common" ? ALWAYS : r.storey;
      this.add(mesh, r.kind, storey, { kind: "run", r }, [r.tag, r.from, r.to, g?.key ?? ""]);
      this.runMeshes.set(r.tag, mesh);
    }
  }

  groupOfRun(r: Run): Group | undefined {
    const d = this.d;
    return d.groups.find((g) => g.odu && (r.from === g.odu.tag || r.to === g.odu.tag || g.heads.some((h) => h.tag === r.from || h.tag === r.to) || g.odu.boxes.some((bx) => bx.tag === r.to || bx.tag === r.from)));
  }
  runsOf(g: Group) { return this.d.runs.filter((r) => this.groupOfRun(r) === g).map((r) => r.tag); }

  // ---------------------------------------------------------------- visibility + selection
  apply() {
    const top = this.b.storeys.length - 1;
    for (const o of this.objects) {
      const { layer, storey } = o.userData as { layer: string; storey: number };
      const sOK = this.storey === "all" || storey === ALWAYS || storey === this.storey || (this.below && storey < this.storey);
      let vis = this.layerOn[layer] && sOK;
      if (layer === "labels") {
        const tags: string[] = o.userData.tags ?? []; const t = tags[0] ?? "";
        vis = this.layerOn.labels && (this.storey === "all" || storey === ALWAYS || storey === this.storey);
        if (/^CU-/.test(t)) vis &&= this.layerOn.heads; else if (/^ODU-/.test(t)) vis &&= this.layerOn.outdoor; else if (/^EF-/.test(t)) vis &&= this.layerOn.fans;
        else if (/^BB-/.test(t)) vis &&= this.layerOn.boxes; else if (/^RK-/.test(t)) vis &&= this.layerOn.racks; else if (/^CH-/.test(t)) vis &&= this.layerOn.chases;
        // with every storey on, only racks, outdoor units and storeys are tagged (unless a system is selected)
        if (this.storey === "all" && !this.selected && /^(CU|EF|BB|CH)-/.test(t)) vis = false;
        if (this.selected && tags.length && !tags.some((x) => this.selected!.has(x))) vis = false;
      }
      if (layer === "roof" && this.storey !== "all" && this.storey !== top) vis = false;
      o.visible = vis;
      if (o instanceof CSS2DObject) o.element.style.display = vis ? "" : "none";
    }
    for (const [tag, mesh] of this.runMeshes) {
      const m = mesh.material as THREE.MeshLambertMaterial;
      const on = !this.selected || this.selected.has(tag);
      m.opacity = on ? 1 : 0.1; m.depthWrite = on;
    }
  }

  select(tags: string[] | null) {
    this.selected = tags && tags.length ? new Set(tags) : null;
    document.querySelectorAll<HTMLElement>("[data-sel]").forEach((el) => el.classList.toggle("sel", !!this.selected && el.dataset.sel!.split(",").some((t) => this.selected!.has(t))));
    this.apply();
  }

  related(p: Pick): string[] {
    const d = this.d;
    const sys = (g: Group | undefined) => g && g.odu ? [g.key, g.odu.tag, ...g.heads.map((h) => h.tag), ...g.odu.boxes.map((bx) => bx.tag), ...(g.chase ? [g.chase.tag] : []), ...this.runsOf(g)] : [];
    const gOf = (key: string) => d.groups.find((g) => g.key === key);
    if (p.kind === "odu") return sys(gOf(p.o.group));
    if (p.kind === "box") return sys(gOf(p.bx.group));
    if (p.kind === "iu") {
      const g = gOf(p.u.group); const own = d.runs.filter((r) => r.from === p.u.tag || r.to === p.u.tag).map((r) => r.tag);
      const bx = g?.odu?.boxes.find((x) => x.tag === p.u.box);
      const trunk = bx ? d.runs.filter((r) => r.to === bx.tag || (r.role === "main" && r.from === g!.odu!.tag)).map((r) => r.tag) : [];
      return [p.u.tag, ...(g?.odu ? [g.odu.tag] : []), ...(bx ? [bx.tag] : []), ...(g?.chase ? [g.chase.tag] : []), ...own, ...trunk];
    }
    if (p.kind === "run") { const g = this.groupOfRun(p.r); return g ? sys(g) : [p.r.tag, p.r.from]; }
    if (p.kind === "fan") return [p.f.tag, ...d.runs.filter((r) => r.from === p.f.tag).map((r) => r.tag)];
    if (p.kind === "room") return d.indoor.filter((u) => u.room.id === p.room.id).flatMap((u) => this.related({ kind: "iu", u }));
    if (p.kind === "rack") return [p.rack.tag, ...d.groups.filter((g) => g.rack === p.rack.tag).flatMap((g) => sys(g))];
    if (p.kind === "chase") return [p.ch.tag, ...p.ch.groups.flatMap((k) => sys(gOf(k)))];
    return [];
  }

  // ---------------------------------------------------------------- info card
  show(p: Pick | null) {
    const el = $("info");
    if (!p) { el.classList.add("hidden"); this.select(null); return; }
    const d = this.d; const S = this.b.storeys;
    const floors = (g: Group) => g.storeys.map((s) => S[s].short).join(" + ");
    let t = "", k = "", rows: [string, string][] = [];
    if (p.kind === "room") {
      const r = p.room; const st = S[r.storey]; const us = d.indoor.filter((u) => u.room.id === r.id);
      t = `${r.name} ${r.number}`; k = `${r.apt ? `Apt ${r.apt} · ` : ""}${st.short}`;
      rows = [["Area", `${r.areaSqFt.toFixed(0)} ft²`],
        ["Ceiling", r.ceilingAboveLevelFt != null ? `${ftIn(r.ceilingAboveLevelFt)} AFF${r.ceilingSource === "assumed" ? " (not modelled — storey typical)" : r.ceilingSource === "partial" ? " (covers part of the room)" : ""}` : "none"],
        ["Ceiling type", r.ceilingType ?? "—"], ["Slab soffit", `${ftIn(r.soffitZ - st.elev)} AFF`], ["Void above ceiling", ftIn(r.voidFt)],
        ["Cassettes", us.length ? us.map((u) => `${u.tag} (${fmt(u.btu)})`).join(", ") : "not conditioned"],
        ["Load (rule of thumb)", `${fmt(Math.round((r.areaSqFt * d.basis.loadPerSqFt) / 100) * 100)} BTU/h @ ${d.basis.loadPerSqFt}/ft²`]];
      if (r.aptNote) rows.push(["Note", r.aptNote]);
    } else if (p.kind === "iu") {
      const u = p.u; const g = d.groups.find((x) => x.key === u.group)!; const o = g.odu!;
      const rl = d.runs.find((r) => r.kind === "refrigerant" && r.role === "indoor" && (r.from === u.tag || r.to === u.tag));
      t = `${u.tag} · ${fmt(u.btu)} BTU/h`; k = `${u.model}${u.type === "1-way" ? " (single direction)" : " (4-way discharge)"} — ${u.room.name} ${u.room.number}`;
      rows = [["Room load", `${fmt(u.loadBtu)} BTU/h (${u.room.areaSqFt.toFixed(0)} ft² × ${d.basis.loadPerSqFt})`], ["Storey", S[u.storey].short],
        ["Ceiling", `${ftIn(u.room.ceilingAboveLevelFt ?? 0)} AFF, void ${ftIn(u.room.voidFt)}${u.voidOk ? "" : " — too shallow"}`], ["Clear to walls", ftIn(u.clearanceFt)],
        ["System", `${o.tag} — ${o.label}`], ...(u.box ? [["Branch box", u.box] as [string, string]] : []),
        ["Line set", `${u.liquidIn} liquid + ${u.gasIn} suction`], ["Run length", rl ? `${rl.lengthFt.toFixed(0)} ft` : "—"],
        ["Condensate", u.riser ? `3/4" to the main, riser ${u.riser}` : "—"]];
    } else if (p.kind === "odu") {
      const o = p.o; const g = d.groups.find((x) => x.key === o.group)!; const rs = d.runs.filter((r) => this.runsOf(g).includes(r.tag) && r.kind === "refrigerant");
      t = `${o.tag} · ${o.label}`; k = `${g.label} · ${floors(g)} · ${o.rack} tier ${o.tier + 1}`;
      rows = [["Nominal", `${fmt(o.btu)} BTU/h`], ["Connected", `${fmt(o.connected)} BTU/h (${Math.round((o.connected / o.btu) * 100)} %)`], ["Indoor units", `${o.heads.length} of ${o.ports}: ${o.heads.map((h) => h.tag).join(", ")}`],
        ["Arrangement", o.config === "branch-box" ? `main line set to ${o.boxes.map((x) => x.tag).join(", ")}` : "home-run line set per cassette"],
        ["Piping total", `${rs.reduce((s, r) => s + r.lengthFt, 0).toFixed(0)} ft`], ["Chase", g.chase?.tag ?? "none (ground floor)"], ["Size (W×D×H)", o.size.map((x) => ftIn(x)).join(" × ")]];
    } else if (p.kind === "box") {
      const bx = p.bx; const br = d.runs.find((r) => r.to === bx.tag);
      t = `${bx.tag} · branch box, ${bx.ports} ports`; k = `${bx.group} · ${S[bx.storey].short} · ${bx.room.name} ${bx.room.number}`;
      rows = [["Cassettes", bx.heads.map((h) => `${h.tag} (${h.btu / 1000}k)`).join(", ")], ["Connected", `${fmt(bx.connected)} BTU/h`], ["Hung at", `${ftIn(bx.pos[2] - S[bx.storey].elev)} AFF in a ${ftIn(bx.room.voidFt)} void`],
        ["Branch line", br ? `${br.size}, ${br.lengthFt.toFixed(0)} ft from the joint` : "—"], ["Access", "600 × 600 access panel in the ceiling below"]];
    } else if (p.kind === "rack") {
      const r = p.rack; t = `${r.tag} · ${r.name}`; k = r.kind === "grade" ? "At grade, behind the wall it backs onto" : "On the roof slab";
      rows = [["Outdoor units", r.odus.map((g) => d.groups.find((x) => x.key === g)?.odu?.tag ?? g).join(", ")], ["Length", `${r.length.toFixed(1)} ft (${r.tiers} tier${r.tiers > 1 ? "s" : ""})`], ["Depth", ftIn(r.depth)],
        ["Height", ftIn(r.legs + r.tiers * r.tierH)], ["Base", `${ftIn(r.z)} (project elevation)`], ...(r.kind === "grade" ? [["Wall behind", `${r.availableFt.toFixed(1)} ft clear run`] as [string, string]] : [])];
    } else if (p.kind === "shaft") {
      const sh = p.sh; t = `${sh.tag} · general riser shaft`; k = `${ftIn(sh.rect.x1 - sh.rect.x0)} × ${ftIn(sh.rect.y1 - sh.rect.y0)} clear, walled on ${sh.modelled.map((s) => S[s].short).join(" + ")}`;
      rows = [["Carries", sh.carries.join(", ") || "—"], ["Extend enclosure", sh.extendOn.map((e) => `${S[e.storey].short} (${e.room})`).join("; ") || "—"], ["Top", `exhaust cap EL. ${ftIn(sh.z1)}`],
        ["Extract", `${d.fans.filter((f) => f.shaft === sh.tag).length} fans, ${d.fans.filter((f) => f.shaft === sh.tag).reduce((t2, f) => t2 + f.cfm, 0)} CFM`]];
    } else if (p.kind === "chase") {
      const c = p.ch; t = `${c.tag} · line-set chase`; k = `serves ${c.groups.join(", ")}`;
      rows = [["Carries", `${c.runs.length} line sets: ${c.runs.join(", ")}`], ["Size", `${ftIn(c.size[0])} × ${ftIn(c.size[1])} clear`], ["Through", c.through.map((x) => `${S[x.storey].short}: ${x.room}`).join("; ") || "own floor only"],
        ["Fire-stop", "sleeve and fire-stop at every slab"]];
    } else if (p.kind === "run") {
      const r = p.r; t = `${r.tag} · ${r.role === "main" ? "main line set" : r.role === "branch" ? "branch line" : r.kind}`; k = `${r.from} → ${r.to}`;
      rows = [["Size", r.size], ["Length", `${r.lengthFt.toFixed(1)} ft`], ...(r.cfm ? [["Air", `${r.cfm} CFM`] as [string, string]] : []), ...(r.tons ? [["Load", `${r.tons.toFixed(1)} tons`] as [string, string]] : []), ...(r.chase ? [["Chase", r.chase] as [string, string]] : []), ...(r.riser ? [["Riser", r.riser] as [string, string]] : []), ["Route", r.note ?? "—"]];
    } else if (p.kind === "fan") {
      const f = p.f; const ex = d.runs.find((r) => r.from === f.tag)!;
      t = `${f.tag} · ${f.cfm} CFM`; k = "Delta BreezGreenBuilder GBR100H (modelled)";
      rows = [["Room", f.room ? `${f.room.name} ${f.room.number}` : "—"], ["Storey", S[f.storey].short], ["Duct", `${ex?.size ?? "—"}, ${ex ? ex.lengthFt.toFixed(0) : "?"} ft to ${f.terminal === "shaft" ? `the extract main / ${f.shaft}` : "a wall cap"}`]];
    } else { t = p.title; k = p.sub; rows = p.rows; }
    el.innerHTML = `<button class="x" title="Close">×</button><div class="t">${esc(t)}</div><div class="k">${esc(k)}</div><dl>${rows.map(([a, b]) => `<dt>${esc(a)}</dt><dd>${esc(b)}</dd>`).join("")}</dl>`;
    el.classList.remove("hidden");
    el.querySelector(".x")!.addEventListener("click", () => this.show(null));
    this.select(this.related(p));
  }

  // ---------------------------------------------------------------- panel
  buildPanel() {
    const { b, d } = this; const S = b.storeys;
    $("project").textContent = b.model.title.replace(/\.rvt$/i, "");
    $("stats").innerHTML = `${S.length} storeys · ${b.rooms.length} rooms · ${b.model.ceilings.length} ceilings · ${b.model.walls.length} walls<br>` +
      `${d.indoor.length} cassettes (${fmt(d.totals.connectedBtu)} BTU/h) · ${d.outdoor.length} outdoor units on ${d.racks.length} racks · ${d.boxes.length} branch boxes · ${d.chases.length} chases · ${d.fans.length} exhaust fans<br>` +
      `${fmt(Math.round(d.totals.refrigerantFt))} ft line set · ${fmt(Math.round(d.totals.condensateFt))} ft condensate · ${fmt(Math.round(d.totals.exhaustFt))} ft duct`;

    const sb = $("storeys");
    const opts: { k: number | "all"; t: string }[] = [{ k: "all", t: "All" }, ...S.map((s) => ({ k: s.index, t: s.short.replace(/ Floor$/i, "") }))];
    sb.innerHTML = opts.map((o) => `<button data-s="${o.k}">${esc(o.t)}</button>`).join("");
    const markS = () => sb.querySelectorAll<HTMLElement>("button").forEach((x) => x.classList.toggle("on", x.dataset.s === String(this.storey)));
    sb.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement).closest("button") as HTMLElement | null; if (!t) return;
      this.storey = t.dataset.s === "all" ? "all" : +t.dataset.s!; markS(); this.apply();
    });
    markS();
    const below = $<HTMLInputElement>("below"); below.checked = this.below;
    below.addEventListener("change", () => { this.below = below.checked; this.apply(); });

    const counts: Record<string, number> = {}; const seen = new Set<unknown>();
    for (const o of this.objects) { const p = o.userData.pick; const key = p && (p.u ?? p.rack ?? p.o ?? p.bx ?? p.ch ?? p.r ?? p.f ?? p.room ?? p); if (!p || seen.has(key)) continue; seen.add(key); counts[o.userData.layer] = (counts[o.userData.layer] ?? 0) + 1; }
    $("layers").innerHTML = LAYERS.map((l) => `<label class="row"><input type="checkbox" data-l="${l.key}" ${this.layerOn[l.key] ? "checked" : ""}/><span class="sw" style="background:${l.color}"></span>${esc(l.label)}<span class="n">${counts[l.key] || ""}</span></label>`).join("");
    $("layers").addEventListener("change", (e) => { const i = e.target as HTMLInputElement; this.layerOn[i.dataset.l!] = i.checked; this.apply(); });
    $<HTMLInputElement>("ceilOpacity").addEventListener("input", (e) => {
      (this.mats.ceiling as THREE.MeshLambertMaterial).opacity = +(e.target as HTMLInputElement).value;
    });
    $("legend").innerHTML = [["#b4531a", "Main / branch line set (outdoor unit to branch boxes)"], ["#e07b2a", "Line set to a cassette (liquid + suction, insulated)"], ["#60a5fa", "Condensate from a cassette (pump lift, 3/4 in.)"], ["#2f86d6", "Condensate main, 1 % fall to a riser"], ["#1e40af", "Condensate riser / common drain (out above the ground slab)"], ["#a3adb8", "Bathroom extract branch"], ["#4b5563", "Extract main / riser in SH-1 to the roof cap"], ["#19a3a3", "Cassette with discharge arrows (4-way / 1-way)"], ["#7c3aed", "Branch box in a closet / hall ceiling"], ["#d8b4fe", "Line-set chase (internal, fire-stopped at slabs)"], ["#94a3b8", "General riser shaft SH-1"], ["#9a8f80", "Outdoor-unit racks: grade NE / W / N, terrace beside the gym"]]
      .map(([c, t]) => `<div><span style="background:${c}"></span>${esc(t)}</div>`).join("");
    document.querySelectorAll<HTMLElement>("[data-view]").forEach((x) => x.addEventListener("click", () => this.view(x.dataset.view!)));
    document.querySelectorAll<HTMLElement>(".tabs button").forEach((x) => x.addEventListener("click", () => {
      document.querySelectorAll(".tabs button").forEach((y) => y.classList.toggle("on", y === x));
      document.querySelectorAll(".tab").forEach((y) => y.classList.toggle("on", y.id === `tab-${x.dataset.tab}`));
    }));

    // systems: one card per outdoor unit, then the exhaust fans
    $("tab-systems").innerHTML = `<h3>Split systems — ${d.outdoor.length} outdoor units on ${d.racks.map((r) => r.tag).join(", ")}</h3>` +
      d.outdoor.map((o) => {
        const g = d.groups.find((x) => x.key === o.group)!; const rl = (h: IndoorUnit) => d.runs.find((r) => r.role === "indoor" && (r.from === h.tag || r.to === h.tag));
        return `<div class="card" data-sel="${o.tag}" data-odu="${o.tag}"><div class="t"><span>${o.tag} · ${esc(g.label)}</span><span>${o.rack} · ${esc(g.storeys.map((s) => S[s].short.replace(/ Floor$/i, "")).join(" + "))}</span></div>
        <div class="s">${esc(o.label)} · ${fmt(o.connected)} of ${fmt(o.btu)} BTU/h${g.chase ? ` · ${g.chase.tag}` : ""}<br>${o.heads.map((h) => `${h.tag} ${esc(h.room.name)} ${h.btu / 1000}k${h.box ? ` via ${h.box}` : ""} — ${rl(h)?.lengthFt.toFixed(0) ?? "?"} ft`).join("<br>")}</div></div>`;
      }).join("") +
      `<h3>Exhaust — ${d.fans.length} fans</h3><table><tr><th>Fan</th><th>Room</th><th class="r">CFM</th><th class="r">Duct</th></tr>` +
      d.fans.map((f) => `<tr class="pick" data-sel="${f.tag}" data-fan="${f.tag}"><td>${f.tag}</td><td>${esc(f.room ? `${f.room.name} ${f.room.number}` : "—")}</td><td class="r">${f.cfm}</td><td class="r">${d.runs.find((r) => r.from === f.tag)!.lengthFt.toFixed(0)} ft</td></tr>`).join("") + "</table>";
    $("tab-systems").addEventListener("click", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-odu],[data-fan]"); if (!el) return;
      if (el.dataset.odu) this.focus({ kind: "odu", o: d.outdoor.find((x) => x.tag === el.dataset.odu)! });
      else this.focus({ kind: "fan", f: d.fans.find((x) => x.tag === el.dataset.fan)! });
    });

    // rooms: ceiling heights and voids per storey
    $("tab-rooms").innerHTML = S.map((s) => `<h3>${esc(s.short)} — level ${ftIn(s.elev)}, slab soffit ${ftIn(s.soffitZ - s.elev)} AFF</h3>
      <table><tr><th>Room</th><th class="r">ft²</th><th class="r">Ceiling</th><th class="r">Void</th><th class="r">AC</th></tr>` +
      [...s.rooms].sort((a, c) => a.number.localeCompare(c.number, undefined, { numeric: true })).map((r) => {
        const n = d.indoor.filter((u) => u.room.id === r.id).length;
        return `<tr class="pick" data-room="${r.id}"><td>${esc(r.number)} ${esc(r.name)}</td><td class="r">${r.areaSqFt.toFixed(0)}</td><td class="r">${r.ceilingAboveLevelFt != null ? ftIn(r.ceilingAboveLevelFt) : "—"}${r.ceilingSource === "assumed" ? "*" : ""}</td><td class="r">${ftIn(r.voidFt)}</td><td class="r">${n || ""}</td></tr>`;
      }).join("") + "</table>").join("") + `<p class="stats">* no ceiling modelled over the room — the storey's typical height is assumed.</p>`;
    $("tab-rooms").addEventListener("click", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>("[data-room]"); if (!el) return;
      this.focus({ kind: "room", room: b.rooms.find((r) => r.id === +el.dataset.room!)! });
    });

    // checks
    const warn = d.findings.filter((f) => f.level === "warn"), info = d.findings.filter((f) => f.level === "info");
    $("tab-checks").innerHTML = `<h3>${warn.length} warnings · ${info.length} notes</h3>` +
      [...warn, ...info].map((f) => `<div class="finding ${f.level}" ${f.ref ? `data-ref="${f.ref}" style="cursor:pointer"` : ""}>${esc(f.text)}</div>`).join("") +
      `<h3>Design basis</h3><div class="stats">Rooms, walls, slabs and ceilings come from the Revit model; the modelled wall heads are replaced. One compact 4-way cassette per conditioned room, sized from ${d.basis.loadPerSqFt} BTU/h·ft² (rule of thumb, not a Manual J); it needs a ${ftIn(d.basis.minVoidFt)} void. One multi-split outdoor unit per apartment on racks at grade (north-east corner, west side) or on the roof — none on the north, south or east façades. Small apartments are home-run; larger ones use branch boxes in closet / hall ceilings. Piping runs in the ceiling voids to internal chases, never on a façade. Limits: direct 82 ft per unit, 230 ft per system, 49 ft height; branch-box 49 ft box to unit, 180 ft to the farthest box, 377 ft in all, 98 ft height; 32 ft between indoor units. Brand-neutral — verify against the chosen make.</div>`;
    $("tab-checks").addEventListener("click", (e) => {
      const ref = (e.target as HTMLElement).closest<HTMLElement>("[data-ref]")?.dataset.ref; if (!ref) return;
      const u = d.indoor.find((x) => x.tag === ref); const o = d.outdoor.find((x) => x.tag === ref); const f = d.fans.find((x) => x.tag === ref);
      if (u) this.focus({ kind: "iu", u }); else if (o) this.focus({ kind: "odu", o }); else if (f) this.focus({ kind: "fan", f });
    });
  }

  focus(p: Pick) {
    // isolate the storey the system serves so its routes are not hidden inside the floors above
    const st = p.kind === "iu" ? p.u.storey : p.kind === "fan" ? p.f.storey : p.kind === "room" ? p.room.storey
      : p.kind === "odu" ? Math.max(...this.d.groups.find((g) => g.key === p.o.group)!.storeys) : p.kind === "box" ? p.bx.storey : null;
    if (st != null && this.storey !== st) {
      this.storey = st; document.querySelectorAll<HTMLElement>("#storeys button").forEach((x) => x.classList.toggle("on", x.dataset.s === String(st)));
    }
    this.show(p);
    const box = new THREE.Box3(); const tags = new Set(this.related(p));
    for (const o of this.objects) if (o.visible && !(o instanceof CSS2DObject) && (o.userData.tags as string[]).some((t) => tags.has(t))) box.expandByObject(o);
    if (p.kind === "room") { const r = p.room; for (const q of r.poly) box.expandByPoint(this.v([q[0], q[1], this.b.storeys[r.storey].elev])); box.expandByPoint(this.v([r.centre[0], r.centre[1], r.ceilingZ])); }
    if (box.isEmpty()) return;
    const c = box.getCenter(new THREE.Vector3()); const size = Math.max(box.getSize(new THREE.Vector3()).length(), 25);
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.controls.target.copy(c); this.camera.position.copy(c.clone().add(dir.multiplyScalar(size * 1.3)));
  }

  view(kind: string) {
    const bx = new THREE.Box3(); for (const o of this.objects) if (o.visible && !(o instanceof CSS2DObject)) bx.expandByObject(o);
    const c = bx.getCenter(new THREE.Vector3()); const r = bx.getSize(new THREE.Vector3()).length() * 0.85;
    const dirs: Record<string, THREE.Vector3> = { iso: new THREE.Vector3(-0.75, 0.62, 0.9), top: new THREE.Vector3(0, 1, 0.0001), front: new THREE.Vector3(0, 0.15, 1), side: new THREE.Vector3(1, 0.15, 0) };
    this.controls.target.copy(c); this.camera.position.copy(c.clone().add(dirs[kind].normalize().multiplyScalar(r)));
  }

  start() {
    this.buildArchitecture(); this.buildHvac(); this.buildPanel(); this.apply();
    const stage = $("stage");
    const resize = () => {
      const w = stage.clientWidth, h = stage.clientHeight;
      this.renderer.setSize(w, h); this.labels.setSize(w, h); this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
    };
    new ResizeObserver(resize).observe(stage); resize();
    this.view("iso");
    const ray = new THREE.Raycaster(); const ptr = new THREE.Vector2(); let down: [number, number] | null = null;
    this.renderer.domElement.addEventListener("pointerdown", (e) => (down = [e.clientX, e.clientY]));
    this.renderer.domElement.addEventListener("pointerup", (e) => {
      if (!down || Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 4) return;
      const r = this.renderer.domElement.getBoundingClientRect();
      ptr.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      ray.setFromCamera(ptr, this.camera);
      const hits = ray.intersectObjects(this.pickables.filter((o) => o.visible), false);
      if (!hits.length) { this.show(null); return; }
      const first = hits[0].distance;
      const hit = hits.find((h) => h.object.userData.pick.kind !== "room" && h.distance < first + 3) ?? hits[0];
      this.show(hit.object.userData.pick as Pick);
    });
    addEventListener("keydown", (e) => { if (e.key === "Escape") this.show(null); });
    const loop = () => { this.controls.update(); this.renderer.render(this.scene, this.camera); this.labels.render(this.scene, this.camera); requestAnimationFrame(loop); };
    loop();
    (window as unknown as { hvac: unknown }).hvac = { b: this.b, d: this.d, viewer: this };
  }
}

main().catch((e) => { $("project").textContent = `Could not load the model: ${e.message}`; console.error(e); });
